import React, { useState, useRef, useEffect } from "react";

const noneSelectedText = "Nothing selected";

function filterOptions(options, search) {
  return options.filter(
    (opt) =>
      opt.label.toLowerCase().includes(search.toLowerCase()) ||
      opt.value.toLowerCase().includes(search.toLowerCase())
  );
}

const Select = ({
  options,
  value,
  onChange,
  id,
  name,
  className = "",
  placeholder,
  disabled = false,
  liveSearch = false,
}) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [focusIdx, setFocusIdx] = useState(-1);
  const btnRef = useRef();
  const menuRef = useRef();

  const filtered = liveSearch ? filterOptions(options, search) : options;

  useEffect(() => {
    if (!open) setSearch("");
    if (open && menuRef.current) menuRef.current.focus();
  }, [open]);

  // Keyboard navigation
  const handleKeyDown = (e) => {
    if (
      !open &&
      (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ")
    ) {
      setOpen(true);
      setFocusIdx(0);
      e.preventDefault();
      return;
    }
    if (!open) return;
    if (e.key === "Escape") {
      setOpen(false);
      btnRef.current.focus();
    } else if (e.key === "ArrowDown") {
      setFocusIdx((i) => Math.min(i + 1, filtered.length - 1));
      e.preventDefault();
    } else if (e.key === "ArrowUp") {
      setFocusIdx((i) => Math.max(i - 1, 0));
      e.preventDefault();
    } else if (e.key === "Enter" || e.key === " ") {
      if (filtered[focusIdx]) {
        onChange({ target: { value: filtered[focusIdx].value, name } });
        setOpen(false);
        btnRef.current.focus();
      }
      e.preventDefault();
    } else if (e.key.length === 1 && /[a-zA-Z0-9]/.test(e.key)) {
      // First-letter jump
      const idx = filtered.findIndex(
        (opt) => opt.label[0].toLowerCase() === e.key.toLowerCase()
      );
      if (idx !== -1) setFocusIdx(idx);
    }
  };

  // Click outside to close
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(e.target) &&
        btnRef.current &&
        !btnRef.current.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  const selected = options.find((opt) => opt.value === value);

  return (
    <div
      className={`btn-group bootstrap-select ${
        open ? "open" : ""
      } ${className}`}
    >
      <button
        type="button"
        className="btn btn-default dropdown-toggle"
        id={id}
        name={name}
        ref={btnRef}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
        onKeyDown={handleKeyDown}
        tabIndex={0}
      >
        <span className="filter-option pull-left">
          {selected ? selected.label : placeholder || noneSelectedText}
        </span>
        <span className="bs-caret">
          <span className="caret"></span>
        </span>
      </button>
      {open && (
        <div
          className="dropdown-menu open"
          tabIndex={-1}
          ref={menuRef}
          style={{ minWidth: 180 }}
          onKeyDown={handleKeyDown}
        >
          {liveSearch && (
            <div className="bs-searchbox">
              <input
                type="text"
                className="form-control"
                autoFocus
                placeholder="Search..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setFocusIdx(0);
                }}
                onKeyDown={(e) => e.stopPropagation()}
              />
            </div>
          )}
          <ul className="dropdown-menu inner" role="listbox">
            {filtered.length === 0 && (
              <li className="no-results">
                <span>No results matched</span>
              </li>
            )}
            {filtered.map((opt, idx) => (
              <li
                key={opt.value}
                className={
                  (opt.value === value ? "selected " : "") +
                  (focusIdx === idx ? "active " : "")
                }
                role="option"
                aria-selected={opt.value === value}
                onMouseDown={() => {
                  onChange({ target: { value: opt.value, name } });
                  setOpen(false);
                  btnRef.current.focus();
                }}
                onMouseEnter={() => setFocusIdx(idx)}
              >
                <a tabIndex={-1} href="#" onClick={(e) => e.preventDefault()}>
                  {opt.label}
                  {opt.value === value && (
                    <span
                      className="glyphicon glyphicon-ok check-mark"
                      style={{ marginLeft: 8 }}
                    />
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

export default Select;
